// Question ViewModel - Presentation logic for single question display

import {
  OnboardingQuestion,
  OnboardingQuestionType,
  OnboardingResponse,
  OnboardingAnswer,
} from '../model/Onboarding.types';

// Single answer option VM
export type AnswerVM = {
  id: string;
  title: string;
  subtitle: string | null;
  isSelected: boolean;
};

// Single question VM
export type QuestionVM = {
  id: string;
  title: string;
  subtitle: string | null;
  type: OnboardingQuestionType;
  answers: AnswerVM[];
  selectedAnswerIds: string[];
  textValue: string | null;
  isRequired: boolean;
  canProceed: boolean;
};

// Create AnswerVM from domain answer
export const createAnswerVM = (
  answer: OnboardingAnswer,
  isSelected: boolean,
): AnswerVM => ({
  id: answer.id,
  title: answer.title,
  subtitle: answer.subtitle,
  isSelected,
});

// Create QuestionVM from domain question and response
export const createQuestionVM = (
  question: OnboardingQuestion,
  response: OnboardingResponse | null,
): QuestionVM => {
  const selectedAnswerIds = response?.answerIds ?? [];
  const selectedSet = new Set(selectedAnswerIds);
  const textValue = response?.textValue ?? null;

  const answers = question.answers.map((a) =>
    createAnswerVM(a, selectedSet.has(a.id)),
  );

  // Can proceed if:
  // - Question is not required, OR
  // - For SINGLE_CHOICE or MULTI_CHOICE: at least one answer is selected
  // - For OPEN_TEXT: text value is provided
  const canProceed =
    !question.isRequired ||
    ((question.type === 'SINGLE_CHOICE' || question.type === 'MULTI_CHOICE') &&
      selectedAnswerIds.length > 0) ||
    (question.type === 'OPEN_TEXT' && textValue !== null && textValue !== '');

  return {
    id: question.id,
    title: question.title,
    subtitle: question.subtitle,
    type: question.type,
    answers,
    selectedAnswerIds,
    textValue,
    isRequired: question.isRequired,
    canProceed,
  };
};

// Check if question has been answered
export const isQuestionAnswered = (
  question: OnboardingQuestion,
  response: OnboardingResponse | null,
): boolean => {
  if (!response) return false;

  if (question.type === 'SINGLE_CHOICE' || question.type === 'MULTI_CHOICE') {
    return response.answerIds.length > 0;
  }

  if (question.type === 'OPEN_TEXT') {
    return response.textValue !== null && response.textValue !== '';
  }

  return false;
};

// Get progress data for question flow
export type QuestionProgressVM = {
  current: number; // 1-indexed for display
  total: number;
  percentage: number;
  completedSteps: number[];
};

export const createQuestionProgressVM = (
  currentIndex: number,
  questions: OnboardingQuestion[],
  responses: OnboardingResponse[],
): QuestionProgressVM => {
  const total = questions.length;
  const current = currentIndex + 1; // 1-indexed for display

  const completedSteps: number[] = [];
  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    const response = responses.find((r) => r.questionId === question.id);
    if (isQuestionAnswered(question, response ?? null)) {
      completedSteps.push(i);
    }
  }

  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  return {
    current,
    total,
    percentage,
    completedSteps,
  };
};
